import React from 'react';
import {
  Sparkles,
  Waves,
  Activity,
  Boxes,
  GraduationCap,
  ArrowUpRight,
  Check,
} from 'lucide-react';
import { SERVICES } from '../mock';

const iconMap = { Sparkles, Waves, Activity, Boxes, GraduationCap };

const ServiceCard = ({ service, featured }) => {
  const Icon = iconMap[service.icon] || Sparkles;
  return (
    <div
      className={`group relative rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur p-6 lg:p-7 overflow-hidden transition-all duration-300 hover:border-slate-700 hover:-translate-y-0.5 ${
        featured ? 'lg:col-span-2' : ''
      }`}
    >
      {/* Gradient blob */}
      <div
        className={`pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-25 blur-3xl bg-gradient-to-br ${service.accent}`}
      />
      {/* Grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,#94a3b8_1px,transparent_1px),linear-gradient(to_bottom,#94a3b8_1px,transparent_1px)] [background-size:32px_32px]"
      />

      <div className="relative">
        <div
          className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${service.accent} items-center justify-center shadow-lg`}
        >
          <Icon size={22} className="text-white" strokeWidth={2} />
        </div>

        <h3 className="mt-5 font-display text-[22px] lg:text-[24px] font-semibold text-white tracking-tight">
          {service.title}
        </h3>
        <p className="mt-1 text-[13.5px] text-slate-400">{service.tagline}</p>

        <p className="mt-4 text-[14.5px] leading-relaxed text-slate-300">
          {service.description}
        </p>

        <ul className="mt-5 space-y-2">
          {service.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-[13.5px] text-slate-300">
              <span className="mt-[3px] flex-none w-4 h-4 rounded-full bg-white/10 flex items-center justify-center">
                <Check size={11} className="text-white" strokeWidth={3} />
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <a
          href="#contact"
          className="mt-6 inline-flex items-center gap-1 text-[13.5px] font-medium text-white/90 hover:text-white transition-colors"
        >
          Explore this service
          <ArrowUpRight
            size={15}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <section id="services" className="relative py-24 lg:py-32 bg-slate-950 overflow-hidden">
      {/* Ambient light */}
      <div className="absolute inset-0 -z-0 opacity-40">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-indigo-600 blur-[120px] opacity-30" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-cyan-500 blur-[120px] opacity-20" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/80 backdrop-blur px-3 py-1 text-[12px] font-medium text-slate-300 uppercase tracking-[0.18em]">
            Services
          </div>
          <h2 className="mt-4 font-display text-[36px] md:text-[52px] leading-[1.05] font-semibold tracking-tight text-white">
            Five practices.{' '}
            <span className="bg-gradient-to-r from-cyan-300 via-indigo-300 to-violet-300 bg-clip-text text-transparent">
              One promise.
            </span>
          </h2>
          <p className="mt-5 text-[16.5px] leading-relaxed text-slate-400 max-w-2xl">
            We ship measurable outcomes - not decks. Every engagement is scoped tight, delivered by senior engineers, and designed to leave your team stronger.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} featured={i === 4} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
